import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { DetailedModelMetrics } from "../../types";
import { InfoTooltip } from "../common/InfoTooltip.tsx";

interface FeatureImportanceChartProps {
  modelData: DetailedModelMetrics | null;
}

// Helper function to prepare and sort data
const prepareChartData = (importance: Record<string, number> | undefined) => {
  if (!importance) return [];
  return (
    Object.entries(importance)
      .map(([name, value]) => ({ name, Wichtigkeit: value }))
      // Sort descending for horizontal bar chart (highest importance at the top)
      .sort((a, b) => b.Wichtigkeit - a.Wichtigkeit)
  );
};

export const FeatureImportanceChart: React.FC<FeatureImportanceChartProps> = ({
  modelData,
}) => {
  if (!modelData || !modelData.feature_importance_model) {
    return (
      <div className="chart-container card">
        <h3>Feature Importance</h3>
        <p>
          Keine Feature Importance Daten im Modell gefunden (erwartet unter
          `feature_importance_model`).
        </p>
      </div>
    );
  }

  const chartData = prepareChartData(modelData.feature_importance_model);

  if (chartData.length === 0) {
    return (
      <div className="chart-container card">
        <h3>Feature Importance</h3>
        {/* Handle case where importance object exists but is empty */}
        <p>Keine Features mit Wichtigkeit '&gt;' 0 gefunden.</p>
      </div>
    );
  }

  return (
    <div className="chart-container card">
      <div className="chart-header-controls">
        <h3>
          Feature Importance
          <InfoTooltip text="Zeigt den relativen Einfluss jedes Eingabe-Features auf die Vorhersagen des Modells. Höhere Werte bedeuten größeren Einfluss." />
        </h3>
      </div>
      {/* Adjust height based on number of features */}
      <ResponsiveContainer width="100%" height={30 + chartData.length * 35}>
        <BarChart
          layout="vertical" // Horizontal bar chart
          data={chartData}
          margin={{
            top: 5,
            right: 30,
            left: 100, // Increased left margin for feature names
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          {/* Corrected YAxis component */}
          <YAxis
            dataKey="name"
            type="category"
            width={150} // Ensure enough space for labels
            interval={0} // Show all labels
          />
          <Tooltip formatter={(value: number) => value.toFixed(4)} />
          <Legend />
          <Bar dataKey="Wichtigkeit" fill="#8884d8" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
